import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BundleComponentsCard } from "@/components/catalogue/BundleComponentsCard";
import { SubscriptionCard } from "@/components/catalogue/SubscriptionCard";

import { toast } from "sonner";
import { Pill } from "@/components/StatusPill";
import { StockStatusPill } from "@/components/StockStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Archive, ArchiveRestore, Loader2, Package, Boxes, Database, ClipboardList, Pencil, MapPin, History } from "lucide-react";
import ProductForm from "@/components/ProductForm";
import S3Uploader from "@/components/S3Uploader";
import { StockActionsBar, useLocations } from "@/components/stock/StockActions";
import { MovementDirection, MovementType, LocationTypePill } from "@/components/stock/StockBadges";
import { useAuth } from "@/context/AuthContext";
import ProductSerials from "@/components/ProductSerials";
import { ProductChannels } from "@/components/ProductChannels";
import { BarcodePreview, BarcodeDialog, FormatSelect } from "@/components/BarcodeTools";

function ProductBarcode({ product }) {
  const [format, setFormat] = useState("auto");
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-barcode-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-slate-900">Barcode</h2>
        {product.barcode && <FormatSelect value={format} onChange={setFormat} testid="product-detail-barcode-format-select" />}
      </div>
      {product.barcode
        ? <BarcodePreview key={product.barcode} value={product.barcode} format={format} height={56} onClick={() => setOpen(true)} testid="product-detail-barcode" />
        : <p className="text-sm text-slate-400" data-testid="product-detail-barcode-empty">No barcode set. Add one by editing the product.</p>}
      {product.barcode && <p className="text-[11px] text-slate-400 mt-2">Click the barcode to print labels or download it.</p>}
      {open && <BarcodeDialog product={product} initialFormat={format} onClose={() => setOpen(false)} />}
    </section>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide truncate">{label}</div>
      <div className={`text-xl font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function ProductImages({ legacyId, canManage, fallback }) {
  const [primary, setPrimary] = useState(null);
  const loadPrimary = useCallback(async () => {
    try {
      const r = await api.get("/attachments", { params: { record_type: "products", record_id: legacyId } });
      const img = (r.data.items || []).find((a) => (a.mime || "").startsWith("image/") && a.scan_status === "NO_THREATS_FOUND");
      if (img) { const v = await api.get(`/attachments/${img.id}/view`); setPrimary(v.data.url); }
      else setPrimary(null);
    } catch { setPrimary(null); }
  }, [legacyId]);
  useEffect(() => { loadPrimary(); }, [loadPrimary]);
  return (
    <div>
      {(primary || fallback) && <img src={primary || fallback} alt="Product" className="w-40 h-40 object-cover rounded-lg border border-slate-200 mb-4" data-testid="product-primary-image" />}
      <p className="text-[11px] text-slate-400 mb-3">Manually uploaded images are private, malware-scanned, and shown in EPOS in preference to WooCommerce images.</p>
      <S3Uploader recordType="products" recordId={legacyId} category="Product image" accept="image/*" canManage={canManage} serverSide onChange={loadPrimary} />
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stock, setStock] = useState(null);
  const [error, setError] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [locations] = useLocations();
  const { can } = useAuth();
  const canManage = can && can("products.manage");
  const { user } = useAuth();
  const canArchive = canManage || user?.is_super || (user?.roles || []).includes("admin");
  const toggleArchive = async () => {
    const p = data.product;
    const isB = p.product_type === "bundle";
    if (!window.confirm(p.archived ? `Restore this product to the product lists?${isB ? " The website bundle will be published again." : ""}` : `Archive this product? It disappears from product lists, EPOS and pickers. Jobs, orders and customers still show it.${isB ? " The website bundle will be moved to the bin." : ""}`)) return;
    try { const r = await api.post("/products/archive", { legacy_ids: [p.legacy_id], archived: !p.archived }); const w = (r.data.website || [])[0]; toast.success(`${p.archived ? "Product restored" : "Product archived"}${w ? (w.status === "ok" ? ` — website ${w.action}` : ` — website update failed: ${w.error}`) : ""}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change archive"); }
  };

  const load = () => {
    setError(false);
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
    api.get(`/products/${id}/stock`).then((r) => setStock(r.data)).catch(() => {});
  };
  useEffect(() => { setData(null); setStock(null); load(); /* eslint-disable-next-line */ }, [id]);

  if (error) return <div className="p-10 text-center" data-testid="product-not-found"><p className="text-slate-500">Product {id} not found.</p><Button variant="outline" className="mt-4" onClick={() => navigate("/products")}>Back to Products</Button></div>;
  if (!data) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading product…</div>;

  const p = data.product;
  const ref = data.legacy_reference;
  const t = stock?.totals || {};
  const money = (v) => (v === null || v === undefined || v === "") ? "—" : `£${Number(v).toFixed(2)}`;

  return (
    <div className="p-6 lg:p-8" data-testid="product-detail">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Package className="w-5 h-5" /></span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="product-detail-title">{p.legacy_id}: {p.title}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {p.category && <Pill colour="slate">{p.category}</Pill>}
              <StockStatusPill status={p.stock_status} />
              {p.serialized && <Pill colour="purple">Serialised</Pill>}
              {p.discontinued && <Pill colour="rose">Discontinued</Pill>}
              {p.service_item && <Pill colour="purple">Service item</Pill>}
              {p.archived && <Pill colour="rose">Archived</Pill>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canArchive && <Button variant="outline" className="gap-1.5" onClick={toggleArchive} data-testid="product-archive-btn">{p.archived ? <><ArchiveRestore className="w-4 h-4" /> Restore</> : <><Archive className="w-4 h-4" /> Archive</>}</Button>}
          <Button variant="outline" className="gap-1.5 shrink-0" onClick={() => setEditOpen(true)} data-testid="product-edit-btn"><Pencil className="w-4 h-4" /> Edit Product</Button>
        </div>
      </div>
      {p.archived && <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="product-archived-banner">Archived{p.archived_by ? ` by ${p.archived_by}` : ""}{p.archived_at ? ` on ${String(p.archived_at).slice(0, 10)}` : ""} — hidden from product lists, EPOS and product pickers. Jobs, orders and customers still show it.</div>}
      {editOpen && <ProductForm product={p} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Boxes className="w-4 h-4 text-emerald-500" /> Stock Summary</h2>
              {p.product_type !== "bundle" && <StockActionsBar productId={p.legacy_id} locations={locations} byLocation={stock?.by_location || []} serialized={p.serialized} customerId={null} onDone={load} />}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Metric label="Total Held" value={t.held ?? 0} />
              <Metric label="Available" value={t.available ?? 0} tone={(t.available ?? 0) > 0 ? "text-emerald-600" : "text-slate-900"} />
              <Metric label="Reserved" value={t.reserved ?? 0} />
              <Metric label="Allocated to Jobs" value={t.allocated ?? 0} tone={(t.allocated ?? 0) > 0 ? "text-amber-600" : "text-slate-900"} />
              <Metric label="On Order" value={t.on_order ?? 0} />
            </div>
            <p className="text-[11px] text-slate-400 mt-3">Total held is the sum of stock across all locations. Available = held − reserved − allocated.</p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-images">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><Package className="w-4 h-4 text-indigo-500" /> Product Images</h2>
            <ProductImages legacyId={p.legacy_id} canManage={canManage} fallback={p.image_url} />
          </section>


          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-by-location">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-indigo-500" /> Stock by Location</h2>
            {(stock?.by_location || []).length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No stock held at any location yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-3">Location</th><th className="py-2 px-2">Type</th>
                    <th className="py-2 px-2 text-right">Held</th><th className="py-2 px-2 text-right">Allocated</th>
                    <th className="py-2 px-2 text-right">Available</th><th className="py-2 px-2 text-right">On Order</th>
                    <th className="py-2 pl-2 text-right">Last Count</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {stock.by_location.map((l) => (
                      <tr key={l.location_id} data-testid={`loc-row-${l.location_id}`}>
                        <td className="py-2 pr-3 font-medium text-slate-800">{l.location_name}</td>
                        <td className="py-2 px-2"><LocationTypePill type={l.location_type} /></td>
                        <td className="py-2 px-2 text-right tabular-nums">{l.qty_held}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-amber-600">{l.qty_allocated || 0}</td>
                        <td className="py-2 px-2 text-right tabular-nums font-semibold text-emerald-600">{l.qty_available}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-slate-500">{l.qty_on_order || 0}</td>
                        <td className="py-2 pl-2 text-right text-xs text-slate-400">{l.last_stocktake_date ? new Date(l.last_stocktake_date).toLocaleDateString("en-GB") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <ProductSerials product={p} locations={locations} canManageProduct={canManage} onEnabled={load} />
          <ProductChannels legacyId={p.legacy_id} />

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-movements">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Recent Movements</h2>
            {(stock?.recent_movements || []).length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No movements recorded yet.</div>
            ) : (
              <div className="divide-y divide-slate-50">
                {stock.recent_movements.map((m) => (
                  <div key={m.movement_id} className="flex items-center justify-between py-2.5 gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap"><MovementType type={m.movement_type} label={m.type_label} /><MovementDirection direction={m.direction} /></div>
                      <div className="text-xs text-slate-400 mt-1 truncate">
                        {m.from_location_name && <>from {m.from_location_name} </>}
                        {m.to_location_name && <>→ {m.to_location_name} </>}
                        {m.job_legacy_id && <>· <Link to={`/tasks/${m.job_legacy_id}`} className="text-indigo-500 hover:underline">{m.job_legacy_id}</Link></>}
                        {m.notes && <> · {m.notes}</>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-semibold tabular-nums ${m.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>{m.direction === "in" ? "+" : "−"}{m.quantity}</div>
                      <div className="text-[11px] text-slate-400">{new Date(m.ts).toLocaleString("en-GB")}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 text-right"><Link to={`/stock/movements?product=${p.legacy_id}`} className="text-sm text-indigo-600 hover:underline">View full history →</Link></div>
          </section>

          <BundleComponentsCard product={p} onSaved={load} />
          <SubscriptionCard product={p} onSaved={load} />

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-related-tasks">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-indigo-500" /> Related Jobs / Orders ({data.related_tasks.length})</h2>
            {data.sales_totals && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="product-sales-totals">
                {[["Jobs", data.sales_totals.jobs], ["Web", data.sales_totals.web], ["Till", data.sales_totals.till]].map(([label, s]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid={`product-sales-${label.toLowerCase()}`}>
                    <div className="text-[11px] font-semibold uppercase text-slate-500">{label}</div>
                    <div className="text-lg font-bold text-slate-900">{s.units} <span className="text-xs font-medium text-slate-500">units</span></div>
                    <div className="text-xs text-slate-500">{s.revenue === null ? "Revenue not recorded on jobs" : `£${s.revenue.toLocaleString("en-GB", { minimumFractionDigits: 2 })} inc VAT`} · {s.count} {label === "Till" ? "sales" : "orders"}{s.test_excluded ? ` · ${s.test_excluded} test excluded` : ""}</div>
                  </div>))}
              </div>)}
            {data.related_tasks.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No related jobs found in the imported data.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.related_tasks.map((tk) => (
                  <Link key={`${tk.kind}-${tk.legacy_id}`} to={tk.link || `/tasks/${tk.legacy_id}`} className="flex items-center justify-between py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-md" data-testid={`product-related-${tk.legacy_id}`}>
                    <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{tk.legacy_id}: {tk.title}</div><div className="text-xs text-slate-400">{tk.kind && <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">{tk.kind}</span>}{String(tk.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                    {tk.status && <Pill colour={tk.status_colour}>{tk.status}</Pill>}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6">
          <ProductBarcode product={p} />
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-base font-bold text-slate-900 mb-3">Product Information</h2>
            <div className="space-y-2 text-sm">
              <Row label="Category" value={p.category} />
              <Row label="SKU" value={p.sku} mono />
              <Row label="Barcode" value={p.barcode} mono />
              <Row label="Brand" value={p.brand} />
              <Row label="Supplier" value={p.supplier} />
              <Row label="Min Stock Level" value={p.min_stock_level ?? "—"} />
              <Row label="Status" value={p.discontinued ? "Discontinued" : (p.active === false ? "Inactive" : "Active")} />
            </div>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-rates">
            <h2 className="text-base font-bold text-slate-900 mb-3">Pricing &amp; Rates</h2>
            <div className="space-y-2 text-sm">
              <Row label="Cost Amount" value={money(p.cost_price)} />
              <Row label="Sell Amount" value={money(p.selling_price)} />
              <Row label="Trade Amount" value={money(p.trade_amount)} />
              <Row label="VAT" value={p.vat_rate != null ? `${p.vat_rate}%` : "—"} />
              <Row label="Unit of Measure" value={p.unit_of_measure} />
              <Row label="Rate Unit" value={p.rate_unit} />
              <Row label="Lock Revenue" value={p.lock_revenue ? "Yes" : "No"} />
            </div>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-woocommerce">
            <h2 className="text-base font-bold text-slate-900 mb-3">WooCommerce</h2>
            <div className="space-y-2 text-sm">
              <Row label="Regular Price" value={money(p.wc_regular_price)} />
              <Row label="Sale Price" value={money(p.wc_sale_price)} />
              <Row label="Published / Draft" value={p.wc_status === "draft" ? "Draft" : (p.wc_status ? "Published" : "—")} />
              <Row label="Stock Quantity" value={p.wc_stock_quantity ?? "—"} />
            </div>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-legacy-reference">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><Database className="w-4 h-4 text-slate-500" /> Legacy Data Reference</h2>
            <div className="space-y-2 text-sm">
              <Row label="Legacy ID" value={ref.legacy_id} mono />
              <Row label="External ID" value={ref.external_id} mono />
              <Row label="Source" value={ref.source_dataset} />
              <div className="flex justify-between"><span className="text-slate-500">Imported</span><span className="text-slate-800 text-right text-xs">{ref.imported_date ? new Date(ref.imported_date).toLocaleString("en-GB") : "—"}</span></div>
            </div>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-media">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-emerald-500" /> Product Images & Media</h2>
            <S3Uploader recordType="products" recordId={p.legacy_id} category="Product Image" canManage={can("products.manage")} accept="image/*,application/pdf" serverSide />
          </section>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className={`text-slate-800 font-medium text-right truncate max-w-[160px] ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

